import { formSlugs } from "@/helpers/utils/form-constants";
import { clearInactiveVillageAnswers, selectVillageVendorFields } from "@/helpers/utils/village-intake";
import { clearInactiveFilmPitchAnswers, selectFilmPitchFields } from "@/helpers/utils/film-constants";
import { STARTUP_ALPHA_RAISING_FIELD, STARTUP_ALPHA_RAISING_FOLLOW_UP } from "@/helpers/utils/startup-alpha-constants";

// Forms whose visible questions depend on one answer. `select` keeps only the
// chosen branch's fields (and none while the answer is unknown); `clear`
// nulls the other branches' answers when the branching answer is saved.
export type FormBranching = {
  field: string;
  select: <T>(fields: Record<string, T>, answer: unknown) => Record<string, T>;
  clear: (answer: unknown) => Record<string, null>;
};

// A Yes/No question whose single follow-up is shown only on Yes and cleared
// on any other answer.
const yesFollowUp = (field: string, followUp: string): FormBranching => ({
  field,
  select: (fields, answer) =>
    answer === "Yes" ? fields : Object.fromEntries(Object.entries(fields).filter(([key]) => key !== followUp)),
  clear: (answer): Record<string, null> => (answer === "Yes" ? {} : { [followUp]: null }),
});

// "Have you been in touch with the C-Tribe team before?" asks who only on Yes.
const inTouchBranching = yesFollowUp("isInTouchWithCTribeTeam", "inTouchWith");
// Startup Alpha: "Are you raising capital now?" asks how much only on Yes.
const startupAlphaBranching = yesFollowUp(STARTUP_ALPHA_RAISING_FIELD, STARTUP_ALPHA_RAISING_FOLLOW_UP);

const FORM_BRANCHING: Record<string, FormBranching> = {
  [formSlugs.vendor]: { field: "exhibitorSegment", select: selectVillageVendorFields, clear: clearInactiveVillageAnswers },
  [formSlugs.filmPitch]: { field: "pitchTrack", select: selectFilmPitchFields, clear: clearInactiveFilmPitchAnswers },
  [formSlugs.fashionDesigner]: inTouchBranching,
  [formSlugs.fashionDesignerFebruary2027]: inTouchBranching,
  [formSlugs.fashionExhibit]: inTouchBranching,
  [formSlugs.fashionPartner]: inTouchBranching,
  [formSlugs.techStartupAlpha]: startupAlphaBranching,
};

export function getFormBranching(formKey: string): FormBranching | undefined {
  return Object.hasOwn(FORM_BRANCHING, formKey) ? FORM_BRANCHING[formKey] : undefined;
}
